import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Newspaper, GraduationCap, Building2, Video, BookOpen, MessageCircleQuestion } from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "../components/ui/command";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const typeIcons = {
  pulse: Newspaper,
  learning: GraduationCap,
  orgPulse: Building2,
  video: Video,
  knowledge: BookOpen,
  ama: MessageCircleQuestion,
};

const typeLabels = {
  pulse: "Today's Pulse",
  learning: "Learning Pathways",
  orgPulse: "Org Pulse",
  video: "Videos",
  knowledge: "Knowledge Base",
  ama: "AMA Questions",
};

export const SearchCommand = ({ open, onOpenChange, onSelectResult }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  const searchContent = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/search`, { query: searchQuery, limit: 15 });
      setResults(response.data.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchContent(query), 300);
    return () => clearTimeout(timer);
  }, [query, searchContent]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  // Group results by type
  const grouped = results.reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  const handleSelect = (item) => {
    onOpenChange(false);
    if (onSelectResult) onSelectResult(item.type, item.data);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search across all content..."
        value={query}
        onValueChange={setQuery}
        data-testid="search-input"
      />
      <CommandList>
        {loading && (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="search-loading">
            Searching...
          </div>
        )}
        {!loading && query.length >= 2 && results.length === 0 && (
          <CommandEmpty data-testid="search-empty">No results found.</CommandEmpty>
        )}
        {!loading && query.length < 2 && (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="search-hint">
            Type at least 2 characters to search
          </div>
        )}
        {Object.entries(grouped).map(([type, items], groupIdx) => {
          const Icon = typeIcons[type] || Newspaper;
          return (
            <div key={type}>
              {groupIdx > 0 && <CommandSeparator />}
              <CommandGroup heading={typeLabels[type] || type}>
                {items.map((item) => (
                  <CommandItem
                    key={`${item.type}-${item.id}`}
                    value={`${item.title} ${item.description}`}
                    onSelect={() => handleSelect(item)}
                    className="cursor-pointer"
                    data-testid={`search-result-${item.type}-${item.id}`}
                  >
                    <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
};
